import LandingPage from "@/components/landing-page/landing-page.vue";
import ErrorPage from "@/components/error-page/error-page.vue";
import DataPolicy from "@/components/data-policy/data-policy.vue";
import VisitDetails from "@/components/visit-details/visit-details.vue";
import VisitorDetails from "@/components/visitor-details/visitor-details.vue";
import InductionPage from "@/components/induction-page/induction-page.vue";
import ConfirmPage from "@/components/confirm-page/confirm-page.vue";
import CompletePage from "@/components/complete-page/complete-page.vue";
import ActionPage from "@/components/action-page/action-page.vue";
import PhotoPage from "@/components/photo-page/photo-page.vue";
import { usePortalStore } from "@/framework/portal";
import { computed, markRaw } from "vue";
import _ from "lodash";

class PortalPageConfig {
	portal: any;

	constructor() {}

	registerErrorPageConfig() {
		this.portal = usePortalStore();
		this.portal.navPageConfig = [
			{
				name: "errorPage",
				component: markRaw(ErrorPage),
				title: "portal_errorTitle",
				description: computed(() => { return this.portal.errorDescription }),
				pageClass: "error-page"
			}
		];
	}

	registerPageConfig() {		
		this.portal = usePortalStore();
		if (!this.portal.referrer.visitorSelfEntry) {
			this.portal.navPageConfig.push({
				name: "landingPage",
				component: markRaw(LandingPage),
				title: "portal_welcomeTitle",
				description: "portal_welcomeDescription",
				pageClass: "landing-page"
			});
		}

		if (this.portal.dataPolicyRequired) {
			this.portal.navPageConfig.push({
				name: "dataPolicy",
				component: markRaw(DataPolicy),
				title: "portal_dataPolicyTitle",
				description: "portal_dataPolicyDescription",
				content: "portal_dataPolicyMessage",
				enabled: !this.portal.dataPolicyAgreed,
				pageClass: "datapolicy-page",
				continue: () => {
					if (this.portal.dataPolicyAgreed === null) {
						this.portal.displayDialog("portal_inductionValidationTitle", "portal_inductionValidationMsg")
					} else {
						this.portal.isValid = true;
					}
				}
			});
		}

		this.portal.navPageConfig.push({
			name: "visitDetails",
			component: markRaw(VisitDetails),
			title: "portal_visitDetailsTitle",
			description: "portal_visitDetailsDescription",
			visitDetails: this.portal.visitDetails,
			enabled: !this.portal.visitDetailsConfirmed,
			pageClass: "visitdetails-page"
		});

		if (this.portal.photoDetails && this.portal.photoDetails.photoCdfId) 
		{
			this.portal.navPageConfig.push({
				name: "photoCapture", 
				component: markRaw(PhotoPage),
				title: "portal_photoCaptureTitle",  
				description: "portal_photoCaptureDescription",
				pageClass: "photocapture-page"
			});
		}

		if (this.portal.fields && this.portal.fields.length) {
			this.portal.navPageConfig.push({
					name: "visitorDetails",
					component: markRaw(VisitorDetails),
					title: "portal_visitorDetailsTitle",
					description: "portal_visitorDetailsDescription",
					fields: this.portal.fields,
					enabled: !this.portal.visitorDetailsCompleted,
					pageClass: "visitor-page",
					continue: () => {
						this.portal.validate();
					}
				});
		}
		if (this.portal.inductions && this.portal.inductions.length) {
			_.forEach(this.portal.inductions, (induction) => {
				induction["isCompleted"] = null;
				induction.agreeButtonText = induction.agreeButtonText === "" ? "portal_btnAgree" : induction.agreeButtonText;
				induction.disagreeButtonText = induction.disagreeButtonText === "" ? "portal_btnDisagree" : induction.disagreeButtonText;

				this.portal.navPageConfig.push({
					name: induction.competencyName,
					component: markRaw(InductionPage),
					title: induction.inductionTitle,
					description: "portal_ndaDescription",
					content: induction.inductionText,
					pageClass: "induction-page",
					pageName: induction.pageName,
					agreeBtnText: induction.agreeButtonText,
					disagreeBtnText: induction.disagreeButtonText,
					skippable: induction.skippable !== false,
					pdfInstructionText: "portal_pdfInstruction",
					continue: () => {
						const currentInduction = this.portal.getInduction(this.portal.currentPageName);

						if (currentInduction.isCompleted === null) {
							this.portal.displayDialog("portal_inductionValidationTitle", "portal_inductionValidationMsg")
						} else {
							this.portal.isValid = true;
						}
					},
					skip: () => {
						_.forEach(this.portal.inductions, (ind) => {
							if (ind.pageName === this.portal.navPageConfig[this.portal.currentNavIndex].pageName) {
								ind.isCompleted = null;
							}
						});
					},
				});
			});
		}

		if (!this.portal.referrer.visitorSelfEntry) {
			this.portal.navPageConfig.push({
				name: "confirmPage",
				component: markRaw(ConfirmPage),
				title: "portal_confirmPageTitle",
				description: "portal_confirmPageDescription",
				visitDetails: this.portal.visitDetails,
				fields: this.portal.fields,
				inductions: this.portal.inductions,
				enabled: true,
				pageClass: "confirmation-page"
			});
		}

		this.portal.navPageConfig.push({
			name: "persistChanges",
			component: markRaw(ActionPage),
			isAction: true,
			continue: () => {
				this.portal._quasar.loading.show();
				const fields: any[] = [];
				const inductions: any[] = [];
				const events: any[] = [];

				_.forEach(this.portal.fields, (fld) => {
					if (fld.isModified){
						if (fld.typeId === 2 && fld.subTypeId === 7 && !!fld.originalValue) fld.originalValue = fld.originalValue.toString();
						fields.push({ cdfRefId: fld.cdfRefId, cdfDetailId: fld.cdfId, cdfValue: fld.originalValue });
					}
				});

				_.forEach(this.portal.inductions, (ind) => {
					if (ind.competencyId && this.portal.allCompetenciesCompleted(ind.competencyId)) {
						const alreadyAdded = _.some(inductions, x => { return x.competencyId === ind.competencyId});
						if (!alreadyAdded)
							inductions.push({ competencyId: ind.competencyId, activationDate: ind.activationDate, expiryDate: ind.expiryDate, defaultTimeZoneId: ind.defaultTimeZoneId, competencyTitle: this.portal._translate(ind.inductionTitle) });
					}
					else if (ind.isCompleted === null) {
						const msg = ind.competencyId ?
							`Portal - ${this.portal._translate(ind.inductionTitle)} Competency Skipped (Id: ${ind.competencyId})` :
							`Portal - ${this.portal._translate(ind.inductionTitle)} Competency Skipped`;
						events.push({ eventName: this.portal.eventNames.ndaDeclinedSkipped, message: msg })
					}
					else if (!ind.isCompleted) {
						const msg = ind.competencyId ?
							`Portal - ${this.portal._translate(ind.inductionTitle)} Competency Declined (Id: ${ind.competencyId})` :
							`Portal - ${this.portal._translate(ind.inductionTitle)} Competency Declined`
						events.push({ eventName: this.portal.eventNames.ndaDeclinedSkipped, message: msg })
					}
				});

				if (this.portal.dataPolicyRequired && !this.portal.dataPolicyAgreed)
					events.push({ eventName: this.portal.eventNames.ndaDeclinedSkipped, message: `Portal - ${this.portal._translate('portal_dataPolicyTitle')} Declined`})

				const dispatchModel = {
					cardholderId: this.portal.payload.cardholderId,
					profileName: this.portal.profileName,
					linkId: this.portal.idParam,
					payload: JSON.stringify(this.portal.payload),
					fields: fields,
					cdfRefIdMap: this.portal.cdfRefMap,
					inductions: inductions,
					events: events,
					tenantId: this.portal.tenantId
				};

				if (this.portal.photoData != null)
				{
					const photoModel = {
						file: this.portal.photoData,
						height: this.portal.photoDetails.height,
						width: this.portal.photoDetails.width,
						tenantId: this.portal.tenantId
					};
					this.SavePhoto(photoModel, dispatchModel);
				}
				else
				{
					this.DispatchCommands(dispatchModel);
				}
			}
		});

		this.portal.navPageConfig.push({
			name: "completePage",
			component: markRaw(CompletePage),
			title: "portal_finalPageTitle",
			description: "portal_finalPageMessage",
			visitDetails: this.portal.visitDetails,
			visitCodeQr: this.portal.visitCodeQr,
			pageClass: "complete-page"
		},
		{
			name: "completeStatusPage",
			component: markRaw(CompletePage),
			title: "portal_completeStatusPageTitle",
			description: "portal_completeStatusPageMessage",
			pageClass: "complete-status-page"
		});
	}

	private SavePhoto(photoModel: any, dispatchModel: any) : void
	{
		fetch(`${this.portal.getApiPath()}externalPortal/savephoto`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(photoModel).replace("data:image/png;base64,", ""),
		})
			.then((response) => response.json())
			.then((fileId) => {
				// Add details for photo command
				dispatchModel.photo = {
					fileId: fileId,
					photoCdfDetailId: this.portal.photoDetails.photoCdfId
				};
				// Send the commands
				this.DispatchCommands(dispatchModel);
			})
			.catch((error) => {
				if (error) console.log("Error:", error);
				// Send the commands without the photo details
				dispatchModel.photo = null;
				this.DispatchCommands(dispatchModel);
			});
	}

	private DispatchCommands(dispatchModel: any) : void
	{
		fetch(`${this.portal.getApiPath()}externalPortal/dispatch`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(dispatchModel),
		})
			.then((response) => response.json())
			.then((result) => {
				this.portal._quasar.loading.hide();
				if (!result) {
					this.portal.currentNavIndex = 0;
					console.log("Error:", result);
				}
				else {
					this.portal.disableHomeButtonClick = true;
				}
			})
			.catch((error) => {
				this.portal._quasar.loading.hide();
				this.portal.currentNavIndex = 0;
				if (error) console.log("Error:", error);
			});
	}
}

export default PortalPageConfig;
export const portalPageConfig: PortalPageConfig = new PortalPageConfig();
